import { Suspense, lazy } from 'react';
import { Navigate } from 'react-router-dom';
import { RouteObject } from 'react-router';

import SidebarLayout from 'src/layouts/SidebarLayout';
import BaseLayout from 'src/layouts/BaseLayout';
import HeaderOnlyLayout from 'src/layouts/HeaderOnlyLayout';
import FullScreenLayout from 'src/layouts/FullScreenLayout';

import SuspenseLoader from 'src/components/SuspenseLoader';
import RequireDeveloper from 'src/components/RequireDeveloper';
import PosThemeScope from 'src/theme/PosThemeScope';

const Loader = (Component) => (props) =>
  (
    <Suspense fallback={<SuspenseLoader />}>
      <Component {...props} />
    </Suspense>
  );

// Setup - Moved from pages/Setup to applications/Setup
const CreateUserRole = Loader(lazy(() => import('./content/applications/Setup/Master/UserRoles/CreateUserRole')));
const RoleList = Loader(lazy(() => import('./content/applications/Setup/Master/UserRoles/RoleList')));
const MenuForm = Loader(lazy(() => import('./content/applications/Setup/Master/AddMenue')));
const CreateDepartment = Loader(lazy(() => import('./content/applications/Setup/Master/Departments/CreateDepartment')));
const OrganizationForm = Loader(lazy(() => import('./content/applications/Setup/Master/CompanyInformation')));
const AllCompany = Loader(lazy(() => import('./content/applications/Setup/Master/AllCompany')));
const CategoryForm = Loader(lazy(() => import('./content/applications/Setup/Master/Category/AddCategory')));
const ApplicationsCategory = Loader(lazy(() => import('./content/applications/Setup/Master/GetCategory')));
const UnitForm = Loader(lazy(() => import('./content/applications/Setup/Master/Units/AddUnits')));
const ApplicationsUnit = Loader(lazy(() => import('./content/applications/Setup/Master/GetUnit')));
const SupplierForm = Loader(lazy(() => import('./content/applications/Setup/Master/Supplier/Supplier')));
const ApplicationsBranch = Loader(lazy(() => import('./content/applications/Setup/Master/GetBranch')));
const GetAllGeneralLedger = Loader(lazy(() => import('./content/applications/Setup/Master/leadgerTransaction/leadgertransaction')));
const GetAllAccounts = Loader(lazy(() => import('./content/applications/Setup/Master/AllAccounts/GetAllAccounts')));
const ItemStockDetails = Loader(lazy(() => import('./content/applications/Setup/ItemStock/GetItemStockById')));
const GetAllSuppliers = Loader(lazy(() => import('./content/applications/Setup/Master/Supplier/GetAllSuppliers')));
const ApplicationsSupplier = Loader(lazy(() => import('./content/applications/Setup/Master/GetSupplier')));
const NewCustomer = Loader(lazy(() => import('./content/applications/Setup/Master/Customers/NewCustomer')));
const CustomerList = Loader(lazy(() => import('./content/applications/Setup/Master/Customers/GetAllCustomers')));
const ApplicationsCustomer = Loader(lazy(() => import('./content/applications/Setup/Master/GetCustomer')));
const TablesPage = Loader(lazy(() => import('./content/applications/Setup/Restaurants/Tables')));
const FloorsPage = Loader(lazy(() => import('./content/applications/Setup/Restaurants/Floors')));
const TableQRCodes = Loader(lazy(() => import('./content/applications/Setup/Restaurants/TableQRCodes')));
const PaymentMethods = Loader(lazy(() => import('./content/applications/Setup/PaymentMethods/PaymentMethods')));
const SetupFeedbackDashboard = Loader(lazy(() => import('./content/applications/Setup/Feedback')));
const ItemDiscountRules = Loader(lazy(() => import('./content/applications/Setup/ItemDiscountRules')));
const ItemTaxRules = Loader(lazy(() => import('./content/applications/Setup/ItemTaxRules')));
const CustomerDiscountRules = Loader(lazy(() => import('./content/applications/Setup/CustomerDiscountRules')));
const InvoiceDiscountRules = Loader(lazy(() => import('./content/applications/Setup/InvoiceDiscountRules')));
const InvoiceTaxRules = Loader(lazy(() => import('./content/applications/Setup/InvoiceTaxRules')));
const TaxOnPaymentMethods = Loader(lazy(() => import('./content/applications/Setup/TaxOnPaymentMethods')));
const PaymentMethodTax = Loader(lazy(() => import('./content/applications/Setup/PaymentMethodTax')));
const ServiceCharges = Loader(lazy(() => import('./content/applications/Setup/ServiceCharges')));
const StockAdjustments = Loader(lazy(() => import('./content/applications/Setup/StockAdjustments')));
const TaxCompliance = Loader(lazy(() => import('./content/applications/Setup/TaxCompliance')));
const OpeningStock = Loader(lazy(() => import('./content/applications/Setup/OpeningStock')));
const VariantPurchasing = Loader(lazy(() => import('./content/applications/Setup/VariantPurchasing')));
const BulkVariantItems = Loader(lazy(() => import('./content/applications/Setup/BulkVariantItems')));
const Bookings = Loader(lazy(() => import('./content/applications/Setup/Bookings')));
const ModernDashboard = Loader(lazy(() => import('./content/applications/ModernDashboard')));
const ModernDashboardDesigner = Loader(lazy(() => import('./content/applications/Developers/ModernDashboardDesigner')));
const SaleAudit = Loader(lazy(() => import('./content/applications/Developers/SaleAudit')));
const CompanyFbrIntegration = Loader(lazy(() => import('./content/applications/Settings/CompanyFbrIntegration')));
const CompanyZatcaIntegration = Loader(lazy(() => import('./content/applications/Settings/CompanyZatcaIntegration')));
const VatRates = Loader(lazy(() => import('./content/applications/Setup/VatRates')));
const FixedAssetsPage = Loader(lazy(() => import('./content/applications/FixedAssets/FixedAssetsPage')));
const SupportingApplications = Loader(lazy(() => import('./content/applications/Setup/SupportingApplications')));

// Auth - Moved from pages/Accounts and pages/RegisterStore to applications/Auth
const RegisterStorePage = Loader(lazy(() => import('./content/applications/Auth/RegisterStorePage')));
const ResetPasswordPage = Loader(lazy(() => import('./content/applications/Auth/ResetPasswordPage')));

// Reports - Moved from pages/Reports to applications/Reports
const ReportsPage = Loader(lazy(() => import('./content/applications/Reports/ReportsPage')));

// Inventory Applications - Moved from WareHouse/Inventory to Inventory
const Purchase = Loader(lazy(() => import('./content/applications/Inventory/Purchase/Purchase')));
const GetAllPurchase = Loader(lazy(() => import('./content/applications/Inventory/Purchase/GetAllPurchase')));
const PurchaseDetails = Loader(lazy(() => import('./content/applications/Inventory/Purchase/PurchaseDetails')));
const PurchaseReturn = Loader(lazy(() => import('./content/applications/Inventory/Return/PurchaseReturn')));
const PurchaseReturnHistory = Loader(lazy(() => import('./content/applications/Inventory/Return/PurchaseReturnHistory')));
const PurchaseReturnDetails = Loader(lazy(() => import('./content/applications/Inventory/Return/PurchaseReturnDetails')));
const TransferStock = Loader(lazy(() => import('./content/applications/Inventory/Stock/TransferStock')));
const GetAllItemStock = Loader(lazy(() => import('./content/applications/Inventory/Stock/GetAllItemStock')));
const GoodsReceived = Loader(lazy(() => import('./content/applications/Inventory/GoodsReceived/GoodsReceived')));

// Barcode Stock Audit (physical inventory count)
const StockAuditList = Loader(lazy(() => import('./content/applications/Inventory/StockAudit/StockAuditList')));
const StockAuditReview = Loader(lazy(() => import('./content/applications/Inventory/StockAudit/StockAuditReview')));
const StockAuditScan = Loader(lazy(() => import('./content/applications/Inventory/StockAudit/StockAuditScan')));
const StockConsumptionList = Loader(lazy(() => import('./content/applications/Inventory/StockConsumption/StockConsumptionList')));
const StockConsumptionForm = Loader(lazy(() => import('./content/applications/Inventory/StockConsumption/StockConsumptionForm')));
const StockConsumptionDetails = Loader(lazy(() => import('./content/applications/Inventory/StockConsumption/StockConsumptionDetails')));
const StockConsumptionReport = Loader(lazy(() => import('./content/applications/Reports/StockConsumptionReport')));

// Items Applications - Moved from WareHouse/Items to Items
const CreateItem = Loader(lazy(() => import('./content/applications/Items/Item/CreateItem')));
const GetAllItems = Loader(lazy(() => import('./content/applications/Items/GetAllItems')));
const ItemDetail = Loader(lazy(() => import('./content/applications/Items/ItemDetail')));
const CreatenNewGroup = Loader(lazy(() => import('./content/applications/Items/Group/CreatenNewGroup')));
const ImportItems = Loader(lazy(() => import('./content/applications/Items/Import/ImportItems')));
const ImportItemsA = Loader(lazy(() => import('./content/applications/Items/Import/ImportItemsA')));

// Sales Applications - Moved from WareHouse/Sales to Sales
const SaleReturn = Loader(lazy(() => import('./content/applications/SaleReturnTerminal/SaleReturn')));
const GetAllSales = Loader(lazy(() => import('./content/applications/Sales/GetAllSales')));
const SaleDetails = Loader(lazy(() => import('./content/applications/Sales/SaleDetails')));
const SaleReturnHistory = Loader(lazy(() => import('./content/applications/Sales/SaleReturnHistory')));
// Exchange module (return + replacement in one transaction)
const ExchangeTerminal = Loader(lazy(() => import('./content/applications/ExchangeTerminal/Exchange')));
const ExchangeHistory = Loader(lazy(() => import('./content/applications/Sales/ExchangeHistory')));
const ExchangeDetails = Loader(lazy(() => import('./content/applications/Sales/ExchangeDetails')));
const ExchangeReport = Loader(lazy(() => import('./content/applications/Reports/ExchangeReport')));
const ExchangeDashboard = Loader(lazy(() => import('./content/applications/Sales/ExchangeDashboard')));
const GetAllTerminals = Loader(lazy(() => import('./content/applications/PaymentTerminal/GetAllTerminals')));
const CreateTerminal = Loader(lazy(() => import('./content/applications/PaymentTerminal/CreateTerminal')));
const TerminalDetails = Loader(lazy(() => import('./content/applications/PaymentTerminal/TerminalDetails')));
const ProcessPayment = Loader(lazy(() => import('./content/applications/PaymentTerminal/ProcessPayment')));
const ReturnDetails = Loader(lazy(() => import('./content/applications/Sales/ReturnDetails')));

// Expense Applications - Moved from WareHouse/Expense to Expense
const CreateExpense = Loader(lazy(() => import('./content/applications/Expense/CreateExpense')));
const GetAllExpense = Loader(lazy(() => import('./content/applications/Expense/GetAllExpense')));

// Cashbook Applications - Moved from WareHouse/Cashbook to Cashbook
const CashbookScreen = Loader(lazy(() => import('./content/applications/Cashbook/Suppliers/SupplierCashbook')));
const SupplierLedger = Loader(lazy(() => import('./content/applications/Cashbook/Suppliers/SupplierLedger')));
const CustomerPaymentScreen = Loader(lazy(() => import('./content/applications/Cashbook/Customers/CustomerPaymentScreen')));
const CustomerLedger = Loader(lazy(() => import('./content/applications/Cashbook/Customers/CustomerLedger')));


// Productions Applications - Moved from WareHouse/Productions to Productions
const Productions = Loader(lazy(() => import('./content/applications/Productions')));
// Production Orders — split out of /production (which is now Recipe Management only)
const ProductionOrders = Loader(lazy(() => import('./content/applications/ProductionOrders')));

// Settings Applications - Moved from WareHouse/CompanySettings to Settings/CompanySettings
const CompanySettings = Loader(lazy(() => import('./content/applications/Settings/CompanySettings')));

// Reports Applications - Moved from WareHouse/Reports to Reports/LabelsPrinting
const LabelPrinting = Loader(lazy(() => import('./content/applications/Reports/LabelsPrinting/LabelPrinting')));

// Other Applications
const Sale = Loader(lazy(() => import('./content/applications/SalesTerminal/SalesTerminal')));
const BalanceSheet = Loader(lazy(() => import('./content/applications/Reports/BalanceSheet')));
const ProftLossStatement = Loader(lazy(() => import('./content/applications/Reports/ProftLossStatement')));
const StockLedgure = Loader(lazy(() => import('./content/applications/Reports/StockLedgure')));
const StockLedgerReport = Loader(lazy(() => import('./content/applications/Reports/StockLedgerReport')));
const BusinessProfile = Loader(lazy(() => import('./content/applications/BusinessSetup/BusinessProfile/BusinessProfile')));
const Upgrade = Loader(lazy(() => import('./content/applications/Subscriptions/Upgrade')));
const DeveloperDashboard = Loader(lazy(() => import('./content/applications/Developers/DeveloperDashboard')));
const DeveloperModules = Loader(lazy(() => import('./content/applications/Developers/DeveloperModules')));
const PackagePlansPage = Loader(lazy(() => import('./content/applications/Developers/PackagePlansPage')));
const FbrIntegration = Loader(lazy(() => import('./content/applications/Developers/FbrIntegration')));
const ZatcaIntegration = Loader(lazy(() => import('./content/applications/Developers/ZatcaIntegration')));
const SumBiteIntegration = Loader(lazy(() => import('./content/applications/Setup/SumBiteIntegration/index')));
const AiItemImages = Loader(lazy(() => import('./content/applications/Setup/AiItemImages')));
const ApiProviders = Loader(lazy(() => import('./content/applications/Developers/ApiProviders')));
const DevLedgerCorrection = Loader(lazy(() => import('./content/applications/Developers/DevLedgerCorrection')));
const OtpVerifications = Loader(lazy(() => import('./content/applications/Developers/OtpVerifications')));
const BillingDashboard = Loader(lazy(() => import('./content/applications/Developers/Billing/BillingDashboard')));
const ErrorLogs = Loader(lazy(() => import('./content/applications/Developers/ErrorLogs')));
const HsCodes = Loader(lazy(() => import('./content/applications/Developers/HsCodes')));
const CompanyDetails = Loader(lazy(() => import('./content/applications/Developers/CompanyDetails')));
const StoresOverviewPage = Loader(lazy(() => import('./content/applications/Developers/StoresOverviewPage')));
const AppUpdates = Loader(lazy(() => import('./content/applications/Developers/AppUpdates')));
const SessionMonitor = Loader(lazy(() => import('./content/applications/Developers/SessionMonitor')));
const CompanyDataTransfer = Loader(lazy(() => import('./content/applications/Developers/CompanyDataTransfer')));
const DataRetention = Loader(lazy(() => import('./content/applications/Developers/DataRetention')));
const DeveloperSettings = Loader(lazy(() => import('./content/applications/Developers/DeveloperSettings')));
// Security Management (rate limits, IP blocking, traffic, events, alerts, audit) — api/developer/security/*
const SecurityOverview = Loader(lazy(() => import('./content/applications/Developers/Security/SecurityOverview')));
const SecurityRateLimits = Loader(lazy(() => import('./content/applications/Developers/Security/RateLimitPolicies')));
const SecurityIpBlocking = Loader(lazy(() => import('./content/applications/Developers/Security/IpBlocking')));
const SecurityTraffic = Loader(lazy(() => import('./content/applications/Developers/Security/TrafficAnalysis')));
const SecurityEvents = Loader(lazy(() => import('./content/applications/Developers/Security/SecurityEvents')));
const SecurityAlerts = Loader(lazy(() => import('./content/applications/Developers/Security/SecurityAlerts')));
const SecurityAudit = Loader(lazy(() => import('./content/applications/Developers/Security/SecurityAudit')));
const NotificationsCenter = Loader(lazy(() => import('./content/applications/Developers/NotificationsCenter')));
const NotificationsInbox = Loader(lazy(() => import('./content/applications/Notifications')));
const DashboardHome = Loader(lazy(() => import('./content/dashboards')));
const AppHomeHub = Loader(lazy(() => import('./content/pages/AppHomeHub')));
const MarketingDashboard = Loader(lazy(() => import('./content/applications/MarketingDashboard')));
const Marketing = Loader(lazy(() => import('./content/applications/Marketing')));
const MarketingCampaigns = Loader(lazy(() => import('./content/applications/Marketing/MarketingCampaigns')));
const CreateCampaign = Loader(lazy(() => import('./content/applications/Marketing/CreateCampaign')));
const MarketingTemplates = Loader(lazy(() => import('./content/applications/Marketing/MarketingTemplates')));
const Terminal3 = Loader(lazy(() => import('./content/applications/SalesTerminal/SalesTerminalIII')));
const WorkShiftPage = Loader(lazy(() => import('./content/applications/SalesTerminal/WorkShift/WorkShiftPage')));
const QROrdering = Loader(lazy(() => import('./content/applications/QROrdering')));
const PaymentAccountsBalances = Loader(lazy(() => import('./content/applications/Reports/PaymentAccountsBalances')));
const CashFlowDashboard = Loader(lazy(() => import('./content/applications/Reports/CashFlowDashboard')));
const TaxDiscountReport = Loader(lazy(() => import('./content/applications/Reports/TaxDiscountReport')));
const ExpiryItemsReport = Loader(lazy(() => import('./content/applications/Reports/ExpiryItemsReport')));
const ZatcaOfflineReport = Loader(lazy(() => import('./content/applications/Reports/ZatcaOfflineReport')));
const SalesReport = Loader(lazy(() => import('./content/applications/Reports/SalesReport')));
const TrialBalance = Loader(lazy(() => import('./content/applications/Reports/TrialBalance')));
const BalanceSheetV2 = Loader(lazy(() => import('./content/applications/Reports/BalanceSheetV2')));
const StockTransferHistoryReport = Loader(lazy(() => import('./content/applications/Reports/StockTransferHistoryReport')));
const SupplierPurchaseReport = Loader(lazy(() => import('./content/applications/Reports/SupplierPurchaseReport')));
const CustomerSalesReport = Loader(lazy(() => import('./content/applications/Reports/CustomerSalesReport')));
const PurchaseOrderReport = Loader(lazy(() => import('./content/applications/Reports/PurchaseOrderReport')));
const StockByBranchReport = Loader(lazy(() => import('./content/applications/Reports/StockByBranchReport')));
const VariantStockReport = Loader(lazy(() => import('./content/applications/Reports/VariantStockReport')));
const UnitWiseStockReport = Loader(lazy(() => import('./content/applications/Reports/UnitWiseStockReport')));
const ProfitabilityReport = Loader(lazy(() => import('./content/applications/Reports/ProfitabilityReport')));
const RecipeMappingReport = Loader(lazy(() => import('./content/applications/Reports/RecipeMappingReport')));
const CompanyTicketDashboard = Loader(lazy(() => import('./content/applications/TicketSupport/CompanyTicketDashboard')));
const SupportTeamDashboard = Loader(lazy(() => import('./content/applications/TicketSupport/SupportTeamDashboard')));
const SalaryManagement = Loader(lazy(() => import('./content/applications/SalaryManagement')));
const UserManagement = Loader(lazy(() => import('./content/applications/UserManagement')));
const CancellationAccess = Loader(lazy(() => import('./content/applications/Setup/CancellationAccess')));
const VoidOrdersDashboard = Loader(lazy(() => import('./content/applications/Setup/VoidOrders')));
const TableOperationsSettings = Loader(lazy(() => import('./content/applications/Setup/TableOperations')));
const ExchangeRulesSettings = Loader(lazy(() => import('./content/applications/Setup/ExchangeRules')));
// Deals module (Setup → Deals): every deal with its components, Excel import / export.
const DealsModule = Loader(lazy(() => import('./content/applications/Setup/Deals')));
const DeliveryCustomers = Loader(lazy(() => import('./content/applications/DeliveryCustomers')));
const GetAllOwnerTransection = Loader(lazy(() => import('./content/applications/ChartOfAccountsTransections/GetAllOwnerTransection')));
const GetAllTransferHistory = Loader(lazy(() => import('./content/applications/ChartOfAccountsTransections/GetAllTransferHistory')));
const CashManagement = Loader(lazy(() => import('./content/applications/CashManagement')));
const CashHandoverReport = Loader(lazy(() => import('./content/applications/Reports/CashHandoverReport')));
const LedgerHistory = Loader(lazy(() => import('./content/applications/Management/LedgerHistory')));
const AiBusinessAssistant = Loader(lazy(() => import('./content/applications/Management/AiBusinessAssistant')));
const GetAllEquity = Loader(lazy(() => import('./content/applications/Setup/Equity/GetAllEquity')));
const QuotationList = Loader(lazy(() => import('./content/applications/SalesDocuments/Quotations/QuotationList')));
const QuotationForm = Loader(lazy(() => import('./content/applications/SalesDocuments/Quotations/QuotationForm')));
const QuotationDetails = Loader(lazy(() => import('./content/applications/SalesDocuments/Quotations/QuotationDetails')));
const SalesOrderList = Loader(lazy(() => import('./content/applications/SalesDocuments/SalesOrders/SalesOrderList')));
const SalesOrderDetails = Loader(lazy(() => import('./content/applications/SalesDocuments/SalesOrders/SalesOrderDetails')));
const InvoiceList = Loader(lazy(() => import('./content/applications/SalesDocuments/Invoices/InvoiceList')));
const InvoiceDetails = Loader(lazy(() => import('./content/applications/SalesDocuments/Invoices/InvoiceDetails')));
const SalesDocReturnList = Loader(lazy(() => import('./content/applications/SalesDocuments/Returns/ReturnList')));
const SalesDocReturnDetails = Loader(lazy(() => import('./content/applications/SalesDocuments/Returns/ReturnDetails')));


// Pages

// Dashboards: imported statically so the home route does not depend on a separate async chunk
// (avoids ChunkLoadError when the dev server restarts or cached bundle references a missing chunk).

// Applications

const UserProfile = Loader(
  lazy(() => import('src/content/applications/Users/profile'))
);
const UserSettings = Loader(
  lazy(() => import('src/content/applications/Users/settings'))
);
const BillingSubscription = Loader(
  lazy(() => import('src/content/applications/Subscriptions/BillingSubscription'))
);

const Setup = Loader(
  lazy(() => import('src/content/applications/Setup'))
);

// Components

const Buttons = Loader(
  lazy(() => import('src/content/pages/Components/Buttons'))
);
const Login = Loader(
  lazy(() => import('src/content/applications/Auth/Login'))
);
const Modals = Loader(
  lazy(() => import('src/content/pages/Components/Modals'))
);
const Accordions = Loader(
  lazy(() => import('src/content/pages/Components/Accordions'))
);
const Tabs = Loader(lazy(() => import('src/content/pages/Components/Tabs')));
const Badges = Loader(
  lazy(() => import('src/content/pages/Components/Badges'))
);
const Tooltips = Loader(
  lazy(() => import('src/content/pages/Components/Tooltips'))
);
const Avatars = Loader(
  lazy(() => import('src/content/pages/Components/Avatars'))
);
const Cards = Loader(lazy(() => import('src/content/pages/Components/Cards')));
const Forms = Loader(lazy(() => import('src/content/pages/Components/Forms')));

// Status

const Status404 = Loader(
  lazy(() => import('src/content/pages/Status/Status404'))
);
const Status500 = Loader(
  lazy(() => import('src/content/pages/Status/Status500'))
);
const StatusComingSoon = Loader(
  lazy(() => import('src/content/pages/Status/ComingSoon'))
);
const StatusMaintenance = Loader(
  lazy(() => import('src/content/pages/Status/Maintenance'))
);

const routes: RouteObject[] = [
  {
    path: '',
    element: <BaseLayout />,
    children: [    
      {
        path: '',
        element: <Navigate to="login" replace />
      },
      {
        path: 'login',
        element: <Login />
      },
      {
        path: 'register-store',
        element: <RegisterStorePage />
      },
      {
        path: 'reset-password',
        element: <ResetPasswordPage  />
      },
      {
        path: 'qr',
        element: <QROrdering />
      },
      {
        path: 'status',
        children: [
          {
            path: '',
            element: <Navigate to="404" replace />
          },
          {
            path: '404',
            element: <Status404 />
          },
          {
            path: '500',
            element: <Status500 />
          },
          {
            path: 'maintenance',
            element: <StatusMaintenance />
          },
          {
            path: 'coming-soon',
            element: <StatusComingSoon />
          }
        ]
      },
      {
        path: '*',
        element: <Status404 />
      }
    ]
  },
  {
    path: 'home',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <AppHomeHub />
      }
    ]
  },
  {
    path: 'dashboard',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <DashboardHome />
      },     
      {
        path: 'touch-pos',
        element: <Navigate to="/dashboard/terminal-3" replace />
      },
      {
        path: 'terminal-3',
        element: <PosThemeScope><Terminal3 /></PosThemeScope>
      },
      {
        path: 'work-shift',
        element: <WorkShiftPage />
      },
    ]
  },
  {
    path: 'touch-pos/full',
    element: <Navigate to="/terminal-3/full" replace />
  },
  // Full-screen Modern Dashboard launcher (store-type aware home screen)
  {
    path: 'modern-dashboard',
    element: <FullScreenLayout />,
    children: [
      {
        path: '',
        element: <ModernDashboard />
      }
    ]
  },
  // Full-screen Terminal 3 without header/sidebar
  {
    path: 'terminal-3/full',
    element: <FullScreenLayout />,
    children: [
      {
        path: '',
        element: <PosThemeScope><Terminal3 /></PosThemeScope>
      }
    ]
  },
  // Full-screen Stock Transfer without header/sidebar
  {
    path: 'inventory/stock/transfer/full',
    element: <FullScreenLayout />,
    children: [
      {
        path: '',
        element: <TransferStock />
      }
    ]
  },
  // Full-screen Purchase without header/sidebar
  {
    path: 'inventory/purchase/full',
    element: <FullScreenLayout />,
    children: [
      {
        path: '',
        element: <Purchase />
      }
    ]
  },
  // Full-screen Stock Audit scan screen (barcode counting)
  {
    path: 'inventory/stock-audit/:id/scan',
    element: <FullScreenLayout />,
    children: [
      {
        path: '',
        element: <StockAuditScan />
      }
    ]
  },
  {
    // Developer-only modules. The gate wraps the layout, so every child route — deep
    // links included — is refused for a non-Developer session (see RequireDeveloper).
    // The sidebar/global search already hide these entries; this closes the URL bar.
    path: 'developer',
    element: (
      <RequireDeveloper>
        <SidebarLayout />
      </RequireDeveloper>
    ),
    children: [
      {
        path: '',
        element: <DeveloperModules />
      },
      {
        path: 'dashboard',
        element: <DeveloperModules />
      },
      {
        path: 'analytics',
        element: <DeveloperDashboard />
      },
      {
        path: 'stores-overview',
        element: <StoresOverviewPage />
      },
      {
        path: 'support-tickets',
        element: <Navigate to="/developer/app-updates" replace />
      },
      {
        path: 'app-updates',
        element: <AppUpdates />
      },
      {
        path: 'session-monitor',
        element: <SessionMonitor />
      },
      {
        path: 'notifications',
        element: <NotificationsCenter />
      },
      {
        path: 'package-plans',
        element: <PackagePlansPage />
      },
      {
        path: 'SupportTeamDashboard',
        element: <SupportTeamDashboard />
      },
      {
        path: 'fbr-integration',
        element: <FbrIntegration />
      },
      {
        path: 'zatca-integration',
        element: <ZatcaIntegration />
      },
      {
        path: 'company/:companyId',
        element: <CompanyDetails />
      },
      {
        path: 'api-providers',
        element: <ApiProviders />
      },
      {
        path: 'ledger-correction',
        element: <DevLedgerCorrection />
      },
      {
        path: 'otp-verifications',
        element: <OtpVerifications />
      },
      {
        path: 'billing',
        element: <BillingDashboard />
      },
      {
        path: 'error-logs',
        element: <ErrorLogs />
      },
      {
        path: 'modern-dashboard-design',
        element: <ModernDashboardDesigner />
      },
      {
        path: 'sale-audit',
        element: <SaleAudit />
      },
      {
        path: 'hs-codes',
        element: <HsCodes />
      },
      {
        path: 'company-data-transfer',
        element: <CompanyDataTransfer />
      },
      {
        path: 'data-retention',
        element: <DataRetention />
      },
      {
        path: 'settings',
        element: <DeveloperSettings />
      },
      // Security Management. The bare path is the Overview (dashboard tile); the sidebar links
      // /security/overview so its prefix-matching NavLink highlights one entry per page.
      {
        path: 'security',
        element: <SecurityOverview />
      },
      {
        path: 'security/overview',
        element: <SecurityOverview />
      },
      {
        path: 'security/rate-limits',
        element: <SecurityRateLimits />
      },
      {
        path: 'security/ip-blocks',
        element: <SecurityIpBlocking />
      },
      {
        path: 'security/traffic',
        element: <SecurityTraffic />
      },
      {
        path: 'security/events',
        element: <SecurityEvents />
      },
      {
        // The in-app notification of High / Critical security alerts links here.
        path: 'security/alerts',
        element: <SecurityAlerts />
      },
      {
        path: 'security/audit',
        element: <SecurityAudit />
      }
    ]
  },
  {
    // The Finance Dashboard was folded into Billing. Existing bookmarks and any
    // link still pointing here land on the module that replaced it rather than
    // on a dead route.
    path: 'finance',
    element: (
      <RequireDeveloper>
        <SidebarLayout />
      </RequireDeveloper>
    ),
    children: [
      { path: '', element: <Navigate to="/developer/billing" replace /> },
      { path: 'dashboard', element: <Navigate to="/developer/billing" replace /> }
    ]
  },
  {
    path: 'notifications',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <NotificationsInbox />
      }
    ]
  },
  {
    // Platform email marketing (campaigns from the platform to stores). Linked only from the
    // developer hub; the API is Developer-only, so the pages are too.
    path: 'marketing',
    element: (
      <RequireDeveloper>
        <SidebarLayout />
      </RequireDeveloper>
    ),
    children: [
      {
        path: '',
        element: <MarketingDashboard />
      },
      {
        path: 'dashboard',
        element: <MarketingDashboard />
      },
      {
        path: 'campaigns',
        element: <Marketing />
      },
      {
        path: 'campaigns/create',
        element: <Marketing />
      },
      {
        path: 'templates',
        element: <Marketing />
      }
    ]
  },
  {
    path: 'support',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <CompanyTicketDashboard />
      },
      {
        path: 'tickets',
        element: <CompanyTicketDashboard />
      }    
    ]
  },

{
  path: 'sale',
  children: [
    // The sale screen uses the new look; the other cashier POS screens keep their original look.
    {
      path: 'create',
      element: <Sale />
    },
    {
      path: 'return',
      element: <PosThemeScope><SaleReturn /></PosThemeScope>
    },
    {
      path: 'exchange',
      element: <PosThemeScope><ExchangeTerminal /></PosThemeScope>
    },
  ]
},

{
  path: 'inventory/return',
  element: <PurchaseReturn />
},

{
  path: 'subscriptions',
  children: [
    {
      path: 'upgrade',
      element: <Upgrade />
    }
  ]
},


  // Items Management
  {
    path: 'items',
    element: <SidebarLayout />,
    children: [
      {
        path: 'create',
        element: <CreateItem />
      },
      {
        path: 'CreateNewGroup',
        element: <CreatenNewGroup />
      },
      {
        path: 'getAll',
        element: <GetAllItems />
      },
      {
        path: 'view/:id',
        element: <ItemDetail />
      },
      {
        path: 'importItems',
        element: <ImportItems />
      },
      {
        path: 'importItemsA',
        element: <ImportItemsA />
      },
    ]
  },

  // Inventory Management
  {
    path: 'inventory',
    element: <SidebarLayout />,
    children: [
      {
        path: 'purchase',
        element: <Purchase />
      },
      {
        path: 'getall',
        element: <GetAllPurchase />
      },
      {
        path:'purchase-details/:id',
        element: <PurchaseDetails />
      },
      {
        path: 'return-history',
        element: <PurchaseReturnHistory />
      },
      {
        path: 'return-details/:id',
        element: <PurchaseReturnDetails />
      },
      {
        path: 'stock',
        children: [
          {
            path: 'transfer',
            element: <TransferStock />
          },
          {
            path: 'items',
            element: <GetAllItemStock />
          },
        ]
      },
      {
        path: 'goods-received',
        element: <GoodsReceived />
      },
      {
        // Stock Manual Consumption — stock used up without a sale. "new" and ":id/edit" come
        // BEFORE ":id" so the literal segment is never swallowed by the parameter.
        path: 'stock-consumption',
        children: [
          {
            path: '',
            element: <StockConsumptionList />
          },
          {
            path: 'new',
            element: <StockConsumptionForm />
          },
          {
            path: ':id/edit',
            element: <StockConsumptionForm />
          },
          {
            path: ':id',
            element: <StockConsumptionDetails />
          },
        ]
      },
      {
        // Barcode Stock Audit. The scan screen is NOT here — it is registered as a
        // top-level FullScreenLayout route (a counter needs the whole viewport and no
        // sidebar to lose scanner focus to).
        path: 'stock-audit',
        children: [
          {
            path: '',
            element: <StockAuditList />
          },
          {
            path: ':id',
            element: <StockAuditReview />
          },
        ]
      },
    ]
  },

  // Sales Management
  {
    path: 'sales',
    element: <SidebarLayout />,
    children: [
      {
        path: 'getAll',
        element: <GetAllSales />
      }, 
      {
        path:'sale-details/:id',
        element: <SaleDetails />
      },
      {
        path: 'returnhistory',
        element: <SaleReturnHistory />
      },
      {
        path: 'return-details/:id',
        element: <ReturnDetails />
      },
      {
        path: 'exchanges',
        element: <ExchangeHistory />
      },
      {
        path: 'exchange-details/:id',
        element: <ExchangeDetails />
      },
      {
        path: 'exchange-dashboard',
        element: <ExchangeDashboard />
      },
    ]
  },

  // Sales Documents (B2B: Quotation -> Sales Order -> Invoice -> Payment)
  {
    path: 'sales-documents',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="quotations" replace />
      },
      {
        path: 'quotations',
        element: <QuotationList />
      },
      {
        path: 'quotations/create',
        element: <QuotationForm />
      },
      {
        path: 'quotations/:id',
        element: <QuotationDetails />
      },
      {
        path: 'quotations/:id/edit',
        element: <QuotationForm />
      },
      {
        path: 'sales-orders',
        element: <SalesOrderList />
      },
      {
        path: 'sales-orders/:id',
        element: <SalesOrderDetails />
      },
      {
        path: 'invoices',
        element: <InvoiceList />
      },
      {
        path: 'invoices/:id',
        element: <InvoiceDetails />
      },
      {
        path: 'returns',
        element: <SalesDocReturnList />
      },
      {
        path: 'returns/:id',
        element: <SalesDocReturnDetails />
      },
    ]
  },

  // Payment Terminal Management
  {
    path: 'payment-terminal',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <GetAllTerminals />
      },
      {
        path: 'create',
        element: <CreateTerminal />
      },
      {
        path: 'edit',
        element: <CreateTerminal />
      },
      {
        path: ':id',
        element: <TerminalDetails />
      },
      {
        path: ':id/process-payment',
        element: <ProcessPayment />
      },
    ]
  },

  // Cashbook Management
  {
    path: 'cashbook',
    element: <SidebarLayout />,
    children: [
      {
        path: 'supplier',
        children: [
          {
            path: 'cashbook/:id',
            element: <CashbookScreen />
          },
          {
            path: 'ledgure/:id',
            element: <SupplierLedger />
          },
        ]
      },
      {
        path: 'customer',
        children: [
          {
            path: 'cashbook/:id',
            element: <CustomerPaymentScreen />
          },
          {
            path: 'ledgure/:id',
            element: <CustomerLedger />
          },
        ]
      },
    ]
  },

  // Expense Management
  {
    path: 'expense',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <GetAllExpense />
      },
      {
        path: 'create',
        element: <CreateExpense />
      },
      {
        path: 'getall',
        element: <GetAllExpense />
      },
    ]
  },

  // Recipe Management (recipes only)
  {
    path: 'production',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <Productions />
      }
    ]
  },

  // Production Orders (its own module)
  {
    path: 'production-orders',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <ProductionOrders />
      }
    ]
  },

  // Settings
  {
    path: 'settings',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <CompanySettings />
      },
      {
        path: 'company',
        element: <CompanySettings />
      },
    ]
  },

  // Reports (single route tree — was duplicated and the second entry overwrote the first in matching)
  {
    path: 'reports',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <ReportsPage />
      },
      {
        path: 'labels',
        element: <LabelPrinting />
      },
      {
        path: 'balancesheet',
        element: <BalanceSheet />
      },
      {
        path: 'profitlossstatement',
        element: <ProftLossStatement />
      },
      {
        path: 'stockLedgure',
        element: <StockLedgure />
      },
      {
        path: 'stock-ledger',
        element: <StockLedgerReport />
      },
      {
        path: 'payment-accounts-balances',
        element: <PaymentAccountsBalances />
      },
      {
        path: 'cash-handover',
        element: <CashHandoverReport />
      },
      {
        path: 'cash-flow',
        element: <CashFlowDashboard />
      },
      {
        path: 'tax-discount-report',
        element: <TaxDiscountReport />
      },
      {
        path: 'expiry-items',
        element: <ExpiryItemsReport />
      },
      {
        path: 'zatca-invoices',
        element: <ZatcaOfflineReport />
      },
      {
        path: 'sales-report',
        element: <SalesReport />
      },
      {
        path: 'trial-balance',
        element: <TrialBalance />
      },
      {
        path: 'balance-sheet-v2',
        element: <BalanceSheetV2 />
      },
      {
        path: 'stock-transfer-history',
        element: <StockTransferHistoryReport />
      },
      {
        path: 'stock-by-branch',
        element: <StockByBranchReport />
      },
      {
        path: 'variant-stock',
        element: <VariantStockReport />
      },
      {
        // Multi Unit Factor: stock in base units + each multi-unit item's other units.
        path: 'unit-wise-stock',
        element: <UnitWiseStockReport />
      },
      {
        // Same report locked to the signed-in user's home branch (no branch picker).
        path: 'my-branch-stock',
        element: <VariantStockReport lockedBranch />
      },
      {
        path: 'supplier-purchase-report',
        element: <SupplierPurchaseReport />
      },
      {
        path: 'customer-sales-report',
        element: <CustomerSalesReport />
      },
      {
        path: 'exchange-report',
        element: <ExchangeReport />
      },
      {
        path: 'purchase-order',
        element: <PurchaseOrderReport />
      },
      {
        path: 'profitability',
        element: <ProfitabilityReport />
      },
      {
        path: 'recipe-mapping',
        element: <RecipeMappingReport />
      },
      {
        path: 'stock-consumption',
        element: <StockConsumptionReport />
      }
    ]
  },

  // Salary Management
  {
    path: 'salary-management',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <SalaryManagement />
      }
    ]
  },

  // User Management (staff & tenant users)
  {
    path: 'user-management',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <UserManagement />
      }
    ]
  },
  {
    path: 'employee-management',
    element: <Navigate to="/user-management" replace />
  },

  // Void Management — Order Cancellation Access
  {
    path: 'management/cancellation-access',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <CancellationAccess />
      }
    ]
  },

  // Void Management — Void Orders Dashboard
  {
    path: 'management/void-orders',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <VoidOrdersDashboard />
      }
    ]
  },

  // Table Operations — Merge / Transfer feature flag
  {
    path: 'management/table-operations',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <TableOperationsSettings />
      }
    ]
  },
  // Exchange Rules — the store's own exchange policy (web + desktop obey it)
  {
    path: 'management/exchange-rules',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <ExchangeRulesSettings />
      }
    ]
  },

  // Deals module (Setup → Deals)
  {
    path: 'management/deals',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <DealsModule />
      }
    ]
  },

  // Delivery Customers Management
  {
    path: 'management/delivery-customers',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <DeliveryCustomers />
      }
    ]
  },

  // POS/Setup Payment Methods
  {
    path: 'pos',
    element: <SidebarLayout />,
    children: [
      {
        path: 'payment-methods',
        element: <PaymentMethods />
      }
    ]
  },

  {
    path: 'management',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="transactions" replace />
      },
      {
        path: 'leadure/:id',
        element: <GetAllGeneralLedger />
      },
      {
        path: 'ledger',
        element: <LedgerHistory />
      }, 
      {
        path: 'AllAccounts',
        element: <GetAllAccounts />
      }, 
      {
        path: 'itemstockbyid/:id',
        element: <ItemStockDetails />
      }, 

      {
        path: 'setup',
      //  element: <Setup />,
        children: [
          {
            path: 'tabs',
            element: <Setup />
          },
          {
            path: 'ai-assistant',
            element: <AiBusinessAssistant />
          },
          {
            path: 'ai-images',
            element: <AiItemImages />
          },
          {
            path: 'restaurants',
            children: [
              { path: '', element: <Navigate to="floors" replace /> },
              { path: 'floors', element: <FloorsPage /> },
              { path: 'tables', element: <TablesPage /> },
              { path: 'table-qr-codes', element: <TableQRCodes /> }
            ]
          },
          {
            path: 'feedback',
            element: <SetupFeedbackDashboard />
          },
          // Expense routes moved to /expense (see main routes)
            {
              path: 'business',
              children: [
                {
                  path: 'profile',
                  element: <BusinessProfile />
                },              
              ]
              },

            {
              path: 'owner-transactions',
              element: <GetAllOwnerTransection />
            },
            {
              path: 'transfer-cash',
              element: <GetAllTransferHistory />
            },
            {
              path: 'cash-management',
              element: <CashManagement />
            },
            {
              path: 'item-discount-rules',
              element: <ItemDiscountRules />
            },
            {
              path: 'item-tax-rules',
              element: <ItemTaxRules />
            },
            {
              path: 'customer-discount-rules',
              element: <CustomerDiscountRules />
            },
            {
              path: 'invoice-discount-rules',
              element: <InvoiceDiscountRules />
            },
            {
              path: 'invoice-tax-rules',
              element: <InvoiceTaxRules />
            },
            {
              path: 'tax-on-payment-methods',
              element: <TaxOnPaymentMethods />
            },
            {
              path: 'payment-method-tax',
              element: <PaymentMethodTax />
            },
            {
              path: 'service-charges',
              element: <ServiceCharges />
            },
            {
              path: 'fbr-integration',
              element: <CompanyFbrIntegration />
            },
            {
              path: 'zatca-integration',
              element: <CompanyZatcaIntegration />
            },
            {
              path: 'sumbite-integration',
              element: <SumBiteIntegration />
            },
            {
              path: 'vat-rates',
              element: <VatRates />
            },
            {
              path: 'fixed-assets',
              element: <FixedAssetsPage />
            },
            {
              path: 'equity',
              children: [
                {
                  path: 'getall',
                  element: <GetAllEquity />
                }
              ]
            },
            {
              path: 'stock-adjustments',
              element: <StockAdjustments />
            },
            {
              path: 'tax-compliance',
              element: <TaxCompliance />
            },
            {
              path: 'opening-stock',
              element: <OpeningStock />
            },
            {
              path: 'variant-purchasing',
              element: <VariantPurchasing />
            },
            {
              path: 'bulk-variant-items',
              element: <BulkVariantItems />
            },
            {
              path: 'bookings',
              element: <Bookings />
            },
            {
              path: 'supporting-applications',
              element: <SupportingApplications />
            },
          {
            path: 'master',
            children: [
              {
                path: 'createuser',
                element: <CreateUserRole />
              },
              {
                path: 'rolelist',
                element: <RoleList />
              },
              {
                path: 'createdepartment',
                element: <CreateDepartment />
              },
              {
                path: 'addmenue',
                element: <MenuForm />
              },
              {
                path: 'Addcompany',
                element: <OrganizationForm />
              },
              {
                path: 'AllCompany',
                element: <AllCompany />
              },            
            ]
          },
          {
            path: 'system',
            children: [
              {
                path: '',
                element: <CategoryForm />
              }, 
              {
                path: 'Addunit',
                element: <UnitForm />
              }, 

              {
                path: 'Addsupplier',
                element: <SupplierForm />
              },
              {
                path: 'getAllSuppliers',
                element: <GetAllSuppliers />
              },
              {
                path: 'allunits',
                element: <ApplicationsUnit
                />
              },

              {
                path: 'allcategory',
                element: <ApplicationsCategory />
              },
              {
                path: 'allsuppliers',
                element: <ApplicationsSupplier />
              },
              {
                path: 'allbranches',
                element: <ApplicationsBranch />
              },
              {
                path: 'allcustomers',
                element: <ApplicationsCustomer />
              },
              {
                path: 'newCustomer',
                element: <NewCustomer />
              },
              {
                path: 'getAllCustomers',
                element: <CustomerList />
              }
            ]
          },

        ]
      },
      {
        path: 'billing',
        element: <BillingSubscription />
      },
      {
        path: 'profile',
        children: [
          {
            path: '',
            element: <Navigate to="details" replace />
          },
          {
            path: 'details',
            element: <UserProfile />
          },
          {
            path: 'settings',
            element: <UserSettings />
          }
        ]
      }
    ]
  },

  {
    path: 'employee',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="crypto" replace />
      },

    ]
  },

  {
    path: '/components',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="buttons" replace />
      },
      {
        path: 'buttons',
        element: <Buttons />
      },

      {
        path: 'modals',
        element: <Modals />
      },
      {
        path: 'accordions',
        element: <Accordions />
      },
      {
        path: 'tabs',
        element: <Tabs />
      },
      {
        path: 'badges',
        element: <Badges />
      },
      {
        path: 'tooltips',
        element: <Tooltips />
      },
      {
        path: 'avatars',
        element: <Avatars />
      },
      {
        path: 'cards',
        element: <Cards />
      },
      {
        path: 'forms',
        element: <Forms />
      }
    ]
  }
];

export default routes;
